// Animate a number from its previous value to a new one with requestAnimationFrame.
// Used by the Overview stat cards so a changing count (deploying now, needs
// attention) draws the eye instead of snapping. Returns the target instantly
// when the user prefers reduced motion.

import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from '../theme/motion';

const DURATION_MS = 600;
// easeOutCubic — quick start, gentle settle.
const ease = (t: number): number => 1 - Math.pow(1 - t, 3);

export const useCountUp = (value: number): number => {
  const reduced = useReducedMotion();
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (reduced || value === fromRef.current) {
      fromRef.current = value;
      setDisplay(value);
      return;
    }
    const from = fromRef.current;
    const delta = value - from;
    let start: number | null = null;

    const step = (ts: number) => {
      if (start === null) start = ts;
      const t = Math.min((ts - start) / DURATION_MS, 1);
      setDisplay(Math.round(from + delta * ease(t)));
      if (t < 1) {
        frameRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = value;
      }
    };

    frameRef.current = requestAnimationFrame(step);
    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [value, reduced]);

  return display;
};
